import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Trophy, Clock, RefreshCw, Search, Crown, Medal, Sliders, Plus, Minus, X, Save } from "lucide-react";
import { toast } from "sonner";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANGES = [
  { key: "day", label: "اليوم" },
  { key: "week", label: "الأسبوع" },
  { key: "month", label: "الشهر" },
  { key: "6months", label: "6 أشهر" },
  { key: "year", label: "السنة" },
  { key: "all", label: "كل الوقت" },
];

const TIERS = [
  { key: "all", label: "كل الفئات" },
  { key: "A_plus", label: "A+ (مميز)" },
  { key: "B_plus", label: "B+ (عادي)" },
  { key: "C_plus", label: "C+ (مبتدئ)" },
];

/**
 * Admin tab: live monthly contest dashboard + ranked markets-by-orders.
 * - Top card: live countdown + "Reset new contest" button (admin only).
 * - Bottom card: ranked table filtered by time range + market-name search.
 */
const TopMarketsTab = () => {
  const token = localStorage.getItem("jumlaty_token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [contest, setContest] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [resetting, setResetting] = useState(false);

  const [range, setRange] = useState("month");
  const [tier, setTier] = useState("all");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);

  // ── Contest state polling ────────────────────────────────────────────
  const fetchContest = async () => {
    try {
      const { data } = await axios.get(`${API}/contest/state`, auth);
      setContest(data);
      setRemaining(Math.max(0, Number(data.remaining_seconds || 0)));
    } catch {
      toast.error("تعذر تحميل حالة السباق");
    }
  };

  useEffect(() => {
    fetchContest();
    const id = setInterval(fetchContest, 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!contest?.is_active) return;
    const id = setInterval(() => {
      setRemaining((r) => Math.max(0, r - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [contest?.is_active]);

  // ── Markets table ────────────────────────────────────────────────────
  const fetchRows = async () => {
    setLoadingRows(true);
    try {
      const params = new URLSearchParams({ range, limit: "200" });
      if (search.trim()) params.set("search", search.trim());
      if (tier && tier !== "all") params.set("tier", tier);
      const { data } = await axios.get(`${API}/admin/top-markets?${params.toString()}`, auth);
      setRows(Array.isArray(data) ? data : []);
    } catch {
      toast.error("تعذر تحميل قائمة الماركتات");
    } finally {
      setLoadingRows(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, tier]);

  const [adjustFor, setAdjustFor] = useState(null); // {user_id, name, current}

  const handleReset = async () => {
    const ok = await confirmDialog("هل تريد بدء سباق جديد لمدة 60 يوم؟ سيتم إعادة العدّ التنازلي.", {
      force: true, tone: "warn", title: "بدء سباق جديد",
      confirmLabel: "نعم، ابدأ السباق", cancelLabel: "إلغاء",
    });
    if (!ok) return;
    setResetting(true);
    try {
      await axios.post(`${API}/admin/contest/reset?duration_days=60`, {}, auth);
      await fetchContest();
      toast.success("تم بدء سباق جديد لمدة 60 يوم");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل بدء السباق");
    } finally {
      setResetting(false);
    }
  };

  const days = Math.floor(remaining / 86400);
  const hours = Math.floor((remaining % 86400) / 3600);
  const minutes = Math.floor((remaining % 3600) / 60);
  const seconds = remaining % 60;
  const pad = (n) => String(n).padStart(2, "0");

  return (
    <div className="space-y-6">
      {/* ── Countdown + Reset card ───────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-l from-[#7e22ce] via-[#5b21b6] to-[#1e1b4b] text-white rounded-3xl p-6 shadow-xl border border-white/10 relative overflow-hidden"
      >
        <div className="absolute -top-10 -right-10 w-44 h-44 bg-amber-300/20 rounded-full blur-3xl pointer-events-none" />

        <div className="relative flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-amber-400 flex items-center justify-center shadow-lg">
              <Trophy className="w-7 h-7 text-amber-900" />
            </div>
            <div>
              <h3 className="text-xl font-extrabold">سباق الماركتات الشهري</h3>
              <p className="text-xs opacity-80">{contest?.is_active ? "جاري السباق — الأكثر طلباً يفوز!" : "انتهى السباق — اضغط زر بدء سباق جديد"}</p>
            </div>
          </div>
          <button
            onClick={handleReset}
            disabled={resetting}
            data-testid="contest-reset-btn"
            className="flex items-center gap-2 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-amber-900 font-extrabold text-sm px-4 py-2 rounded-full transition-colors shrink-0"
          >
            <RefreshCw className={`w-4 h-4 ${resetting ? "animate-spin" : ""}`} />
            بدء سباق جديد (30 يوم)
          </button>
        </div>

        <div className="relative grid grid-cols-4 gap-2 max-w-md" dir="ltr" data-testid="admin-contest-countdown">
          <Cell label="يوم" value={pad(days)} />
          <Cell label="ساعة" value={pad(hours)} />
          <Cell label="دقيقة" value={pad(minutes)} />
          <Cell label="ثانية" value={pad(seconds)} />
        </div>

        {!!contest?.top_3?.length && (
          <div className="relative mt-4 pt-4 border-t border-white/10 grid grid-cols-3 gap-2">
            {contest.top_3.slice(0, 3).map((m, i) => (
              <div key={m.user_id} className="bg-white/10 rounded-2xl p-3 text-center">
                <div className="text-2xl">{i === 0 ? "🥇" : i === 1 ? "🥈" : "🥉"}</div>
                <div className="text-sm font-extrabold truncate mt-1">{m.market_name}</div>
                <div className="text-amber-300 font-extrabold tabular-nums text-base">{m.total_quantity}</div>
              </div>
            ))}
          </div>
        )}
      </motion.div>

      {/* ── Markets ranked table ────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="bg-white rounded-3xl shadow-md border border-[#E4EBE6] p-5"
      >
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-lg font-extrabold text-[#111814]">الماركتات الأكثر طلباً</h3>
          {/* Search */}
          <div className="relative flex-1 max-w-xs min-w-[200px]">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchRows()}
              data-testid="top-markets-search"
              placeholder="ابحث عن اسم ماركت..."
              className="w-full bg-[#F4F1E8] border border-[#E8D9B5] rounded-full pr-10 pl-4 py-2 text-sm focus:outline-none focus:border-[#15803D]"
            />
            <Search className="w-4 h-4 text-[#5C6E64] absolute right-3 top-1/2 -translate-y-1/2" />
            <button
              onClick={fetchRows}
              className="absolute left-1 top-1/2 -translate-y-1/2 bg-[#2D4A3E] hover:bg-[#1f3429] text-white text-xs font-bold px-3 py-1 rounded-full"
              data-testid="top-markets-search-btn"
            >
              بحث
            </button>
          </div>
        </div>

        {/* Range pills */}
        <div className="flex flex-wrap gap-2 mb-3">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              data-testid={`range-${r.key}`}
              className={`text-xs font-bold px-4 py-1.5 rounded-full transition-colors border ${
                range === r.key
                  ? "bg-[#15803D] text-white border-[#15803D]"
                  : "bg-white text-[#15803D] border-[#15803D]/40 hover:bg-[#DCFCE7]"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {/* Tier pills */}
        <div className="flex flex-wrap gap-2 mb-4 pb-3 border-b border-[#E4EBE6]">
          <div className="text-[11px] text-[#5C6E64] font-bold w-full mb-0.5">فلتر حسب الفئة:</div>
          {TIERS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTier(t.key)}
              data-testid={`tier-${t.key}`}
              className={`text-xs font-bold px-4 py-1.5 rounded-full transition-colors border ${
                tier === t.key
                  ? "bg-[#7e22ce] text-white border-[#7e22ce]"
                  : "bg-white text-[#7e22ce] border-[#7e22ce]/40 hover:bg-purple-50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Table */}
        {loadingRows ? (
          <div className="text-center py-8 text-[#5C6E64]">جاري التحميل...</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-8 text-[#5C6E64]">لا توجد بيانات في هذه الفترة.</div>
        ) : (
          <div className="overflow-x-auto" data-testid="top-markets-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F4F1E8] text-[#2D4A3E]">
                  <th className="px-3 py-2 text-right font-bold">المركز</th>
                  <th className="px-3 py-2 text-right font-bold">اسم الماركت</th>
                  <th className="px-3 py-2 text-right font-bold">اسم الزبون</th>
                  <th className="px-3 py-2 text-right font-bold">عدد الطلبات</th>
                  <th className="px-3 py-2 text-right font-bold">إجمالي القطع</th>
                  <th className="px-3 py-2 text-right font-bold">أرباح الماركت</th>
                  <th className="px-3 py-2 text-center font-bold w-16">تعديل</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={r.user_id}
                    data-testid={`top-row-${i}`}
                    className={`border-b border-[#E4EBE6] ${i < 3 ? "bg-gradient-to-l from-amber-50 to-white" : ""}`}
                  >
                    <td className="px-3 py-2.5 font-extrabold text-[#2D4A3E]">
                      {i === 0 ? <Crown className="inline w-4 h-4 text-amber-500" /> :
                        i === 1 ? <Medal className="inline w-4 h-4 text-slate-400" /> :
                        i === 2 ? <Medal className="inline w-4 h-4 text-orange-400" /> :
                        `#${i + 1}`}
                    </td>
                    <td className="px-3 py-2.5 font-bold text-[#111814]">{r.market_name || "—"}</td>
                    <td className="px-3 py-2.5 text-[#5C6E64]">{r.user_name || "—"}</td>
                    <td className="px-3 py-2.5 text-[#2D4A3E] tabular-nums">{r.total_orders}</td>
                    <td className="px-3 py-2.5 text-[#E85D45] font-extrabold tabular-nums">
                      {Number(r.total_quantity || 0).toLocaleString("en-US")}
                      {Number(r.manual_adjustment || 0) !== 0 && (
                        <span
                          className={`ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${Number(r.manual_adjustment) > 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}
                          title="تعديل يدوي"
                        >
                          {Number(r.manual_adjustment) > 0 ? "+" : ""}{r.manual_adjustment}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-[#15803D] font-extrabold tabular-nums" data-testid={`profit-${i}`}>
                      {Number(Math.round(r.total_profit || 0)).toLocaleString("en-US")} د.ع
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <button
                        onClick={() => setAdjustFor({
                          user_id: r.user_id,
                          name: r.market_name || r.user_name || "—",
                          current: Number(r.manual_adjustment || 0),
                          base: Number(r.total_quantity || 0) - Number(r.manual_adjustment || 0),
                        })}
                        data-testid={`adjust-btn-${i}`}
                        title="تعديل يدوي للكراتين"
                        className="w-8 h-8 rounded-lg bg-purple-100 hover:bg-purple-200 text-purple-700 inline-flex items-center justify-center transition-colors"
                      >
                        <Sliders className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>

      {adjustFor && (
        <ManualAdjustModal
          info={adjustFor}
          onClose={() => setAdjustFor(null)}
          onSaved={async () => { setAdjustFor(null); await fetchRows(); await fetchContest(); }}
        />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Manual carton adjustment modal
// -----------------------------------------------------------------------
// Admin can bump a market's contest count up or down by any integer.
// The adjustment stacks on top of the computed cartons — displayed as a
// green/red chip in the table so it's obvious the number was hand-tweaked.
// Backend: PATCH /admin/market-ranking/{user_id} with `manual_adjustment`.
// ═══════════════════════════════════════════════════════════════════════
const ManualAdjustModal = ({ info, onClose, onSaved }) => {
  const token = localStorage.getItem("jumlaty_token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [value, setValue] = useState(info.current || 0);
  const [saving, setSaving] = useState(false);

  const bump = (delta) => setValue((v) => Number(v || 0) + delta);
  const finalTotal = Math.max(0, Number(info.base || 0) + Number(value || 0));

  const submit = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/admin/market-ranking/${info.user_id}`,
        { manual_adjustment: Number(value || 0) },
        auth,
      );
      toast.success("تم حفظ التعديل");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[130] bg-black/60 flex items-center justify-center p-3"
      dir="rtl"
      data-testid="manual-adjust-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl">
        <div className="p-4 text-white relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #7e22ce, #5b21b6)" }}>
          <div className="absolute -top-6 -left-6 w-28 h-28 rounded-full bg-white/10" />
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-white/25 backdrop-blur flex items-center justify-center border-2 border-white/40">
                <Sliders className="w-6 h-6" strokeWidth={2.5} />
              </div>
              <div>
                <p className="text-xs opacity-90">تعديل يدوي للكراتين</p>
                <h3 className="font-black text-lg truncate max-w-[220px]">{info.name}</h3>
              </div>
            </div>
            <button onClick={onClose} data-testid="manual-adjust-close"
              className="w-9 h-9 rounded-full bg-white/25 hover:bg-white/40 backdrop-blur flex items-center justify-center">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-3 bg-slate-50">
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-xl p-2 text-center bg-white border">
              <p className="text-[10px] font-bold text-slate-500">الكراتين الفعلية</p>
              <p className="text-lg font-black text-slate-700 tabular-nums">{info.base}</p>
            </div>
            <div className="rounded-xl p-2 text-center border-2"
              style={{
                background: Number(value) > 0 ? "#f0fdf4" : Number(value) < 0 ? "#fef2f2" : "#f8fafc",
                borderColor: Number(value) > 0 ? "#bbf7d0" : Number(value) < 0 ? "#fecaca" : "#e2e8f0",
              }}>
              <p className="text-[10px] font-bold" style={{
                color: Number(value) > 0 ? "#16a34a" : Number(value) < 0 ? "#dc2626" : "#64748b",
              }}>التعديل اليدوي</p>
              <p className="text-lg font-black tabular-nums" style={{
                color: Number(value) > 0 ? "#16a34a" : Number(value) < 0 ? "#dc2626" : "#64748b",
              }}>{Number(value) > 0 ? "+" : ""}{value}</p>
            </div>
            <div className="rounded-xl p-2 text-center bg-purple-50 border-2 border-purple-200">
              <p className="text-[10px] font-bold text-purple-700">الإجمالي النهائي</p>
              <p className="text-lg font-black text-purple-700 tabular-nums">{finalTotal.toLocaleString("en-US")}</p>
            </div>
          </div>

          {/* Quick +/- buttons */}
          <div className="grid grid-cols-6 gap-1.5">
            {[-10, -5, -1, +1, +5, +10].map((d) => (
              <button
                key={d}
                onClick={() => bump(d)}
                data-testid={`adjust-bump-${d}`}
                className={`py-2 rounded-xl font-black text-sm shadow-sm transition-all ${d > 0 ? "bg-emerald-100 hover:bg-emerald-200 text-emerald-800" : "bg-red-100 hover:bg-red-200 text-red-800"}`}
              >
                {d > 0 ? "+" : ""}{d}
              </button>
            ))}
          </div>

          <div>
            <label className="text-[11px] font-black text-slate-600 mb-1 block">قيمة التعديل (سالب يقلل، موجب يزيد)</label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => bump(-1)}
                className="w-11 h-11 rounded-xl bg-red-100 hover:bg-red-200 text-red-700 flex items-center justify-center shrink-0"
              ><Minus className="w-5 h-5" /></button>
              <input
                type="number"
                value={value}
                onChange={(e) => setValue(e.target.value === "" ? 0 : Number(e.target.value))}
                data-testid="manual-adjust-input"
                className="flex-1 px-3 py-2.5 rounded-xl border-2 border-purple-200 focus:border-purple-500 bg-white text-center text-lg font-black text-purple-700 focus:outline-none tabular-nums"
                dir="ltr"
              />
              <button
                onClick={() => bump(+1)}
                className="w-11 h-11 rounded-xl bg-emerald-100 hover:bg-emerald-200 text-emerald-700 flex items-center justify-center shrink-0"
              ><Plus className="w-5 h-5" /></button>
            </div>
          </div>

          <div className="rounded-xl bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-800">
            💡 هذا التعديل يُضاف/يُطرح من إجمالي كراتين الزبون في المسابقة. الأرقام الفعلية من الطلبات لا تتغير.
          </div>
        </div>

        <div className="p-3 bg-white border-t border-slate-100 grid grid-cols-2 gap-2">
          <button
            onClick={() => setValue(0)}
            data-testid="manual-adjust-reset"
            className="py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm"
          >
            تصفير
          </button>
          <button
            onClick={submit}
            disabled={saving}
            data-testid="manual-adjust-save"
            className="py-2.5 rounded-xl text-white font-black text-sm flex items-center justify-center gap-1.5 shadow-md disabled:opacity-40"
            style={{ background: "linear-gradient(135deg, #7e22ce, #5b21b6)" }}
          >
            <Save className="w-4 h-4" /> {saving ? "..." : "حفظ التعديل"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Cell = ({ label, value }) => (
  <div className="bg-white/15 backdrop-blur-sm rounded-xl py-2 text-center border border-white/10">
    <div className="text-2xl sm:text-3xl font-extrabold tabular-nums leading-none">{value}</div>
    <div className="text-[10px] opacity-80 mt-1">{label}</div>
  </div>
);

export default TopMarketsTab;
